'use client';

import { useEffect, useState } from 'react';

type BlockedRange = { start: string; end: string; source: 'booking' | 'ical' | 'manual' };
type MinNightOverride = { start: string; end: string; minNights: number };

export function useAvailability(params: {
  propertyId: string;
  initialBlocked?: BlockedRange[];
  initialMinNightOverrides?: MinNightOverride[];
  months?: number;
  refreshMs?: number;
  deferMs?: number;
}) {
  const {
    propertyId,
    initialBlocked = [],
    initialMinNightOverrides = [],
    months = 6,
    refreshMs = 60000,
    deferMs = 0,
  } = params;
  const [blocked, setBlocked] = useState<BlockedRange[]>(initialBlocked);
  const [minNightOverrides, setMinNightOverrides] = useState<MinNightOverride[]>(
    initialMinNightOverrides
  );
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let active = true;
    let timeout: number | null = null;
    let interval: number | null = null;
    const fetchAvailability = async () => {
      if (!propertyId) return;
      setLoading(true);
      try {
        const res = await fetch(`/api/properties/${propertyId}/availability?months=${months}`, {
          cache: 'no-cache',
        });
        if (res.status === 304) return;
        if (!res.ok) return;
        const data = await res.json();
        if (!data?.success) return;
        if (!active) return;
        setBlocked(data.data.blocked ?? []);
        setMinNightOverrides(data.data.minNightOverrides ?? []);
      } finally {
        if (active) setLoading(false);
      }
    };

    const start = () => {
      fetchAvailability();
      interval = refreshMs > 0 ? window.setInterval(fetchAvailability, refreshMs) : null;
    };

    if (deferMs > 0) {
      timeout = window.setTimeout(start, deferMs);
    } else {
      start();
    }
    return () => {
      active = false;
      if (timeout) window.clearTimeout(timeout);
      if (interval) window.clearInterval(interval);
    };
  }, [propertyId, months, refreshMs, deferMs]);

  return { blocked, minNightOverrides, loading };
}
